import type { ComponentProps, ForwardedRef, ReactNode } from "react";
import clsx from "clsx";
import { IoMdAdd } from "react-icons/io";
import { TfiMinus } from "react-icons/tfi";

import { LoadingSpinner } from "../LoadingSpinner";
import { TrashIcon } from "../TrashIcon";

import "./styles.css";

export interface ButtonProps extends ComponentProps<"button"> {
  loading?: boolean;
  color?: "primary" | "secondary" | "tertiary" | "destructive" | "text" | "storefront-primary";
  plus?: boolean;
  minus?: boolean;
  rounded?: boolean;
  ref?: ForwardedRef<HTMLButtonElement>;
}

const colorClassName: { [key in Required<ButtonProps>["color"]]: string } = {
  primary: clsx(
    "bg-brand-primary text-store-button-text hover:bg-brand-primary-hover disabled:opacity-50 rounded-xl",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-store-action-and-highlight",
  ),
  "storefront-primary": clsx(
    "bg-store-button text-store-button-text hover:bg-store-button-hover disabled:opacity-50 rounded-xl",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-store-action-and-highlight",
  ),
  secondary: clsx(
    "bg-base-white text-brand-accent outline outline-2 outline-base-input hover:bg-base-input",
    "focus-visible:bg-base-gray active:bg-base-gray disabled:bg-base-white disabled:opacity-50",
  ),
  tertiary: clsx("hover-text-shadow bg-transparent text-brand-accent disabled:opacity-50"),
  destructive: clsx(
    "bg-base-white text-feedback-error outline outline-2 outline-base-input",
    "hover:bg-feedback-error-opacity-10 hover:outline-0 focus-visible:bg-feedback-error-opacity-25 active:bg-feedback-error-opacity-25",
    "disabled:bg-base-white disabled:opacity-50 disabled:outline-0",
  ),
  text: clsx("flex bg-base-white text-start text-feedback-error"),
};

const loadingClassName: { [key in Required<ButtonProps>["color"]]: string } = {
  primary: "disabled:bg-brand-primary",
  "storefront-primary": "disabled:bg-store-button",
  secondary: "disabled:opacity-100 text-brand-variation-1",
  tertiary: "disabled:opacity-100 text-brand-variation-1",
  destructive: "disabled:opacity-100",
  text: "disabled:opacity-100",
};

export const Button = ({
  children,
  className,
  disabled = false,
  loading = false,
  color = "primary",
  plus = false,
  minus = false,
  rounded = false,
  ref,
  ...attributes
}: ButtonProps) => {
  return (
    <button
      {...attributes}
      ref={ref}
      disabled={loading || disabled}
      className={clsx(
        "w-full py-2",
        colorClassName[color],
        loading && loadingClassName[color],
        {
          loading,
          "rounded-full": rounded,
        },
        className,
      )}
    >
      {!loading ? (
        <div className="flex items-center justify-center">
          {["destructive", "text"].includes(color) && (
            <TrashIcon size="1.5rem" className={clsx("fill-feedback-error", { "mr-1": children })} />
          )}

          {plus && <IoMdAdd size="1.5rem" className={children ? "mr-1" : undefined} />}
          {minus && <TfiMinus size="1.5rem" className={children ? "mr-1" : undefined} />}

          {children && <b>{children as ReactNode}</b>}
        </div>
      ) : (
        <LoadingSpinner full />
      )}
    </button>
  );
};
